import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from '@angular/core';
import { forkJoin, lastValueFrom, map } from "rxjs";
import { AppConstants } from '../../../app.constants';
import { ReportCategory, Product, PriceChange, PriceChangeLabel } from "../models/price-change.model";

@Injectable()
export class PriceChangeService {
    private reportCatsUrl: string = "priceChange/reportCat";
    private productsUrl: string = "priceChange/products";
    private updatePricesUrl: string = "priceChange/updatePrices";
    private productHistoryUrl: string = "priceChange/product/history";
    private priceMatchUrl: string = "priceChange/priceMatch";
    private priceChangeLabelsUrl: string = "priceChange/labels/{conceptID}";
    private priceChangeLabelsByDatesUrl: string = "priceChange/labels/dateRange/{conceptID}";
    private addPriceChangeLabelUrl: string = "priceChange/label";
    private getStoreRecordsByLabelsUrl: string = "priceExplorer/history";
    private cancelPriceChangeByStore: string = "priceExplorer/cancel";
    private reschedulePriceChangeByStore: string = "priceExplorer/reschedule";
    private reschedulePriceChangeNewLabel: string = "priceExplorer/reschedule/newLabel";


    private BASE_URL: string;

    constructor(@Inject(HttpClient) private http: HttpClient) {
        this.BASE_URL = AppConstants.BASE_URL;
    }

    async getReportCategories(storeIDs: number[]): Promise<ReportCategory[]> {
        let url = this.BASE_URL + this.reportCatsUrl;
        let data$ = this.http.post<ReportCategory[]>(url, storeIDs)
        const value = await lastValueFrom(data$);
        return value;
    }

    async getProducts(storeIDsAndReportCat: ReportCategory[]): Promise<Product[]> {
        let url = this.BASE_URL + this.productsUrl;
        const batchSize = 50;

        const batches: ReportCategory[][] = [];
        for (let i = 0; i < storeIDsAndReportCat.length; i += batchSize) {
            batches.push(storeIDsAndReportCat.slice(i, i + batchSize));
        }
        const requests = batches.map(batch =>
            this.http.post<Product[]>(url, batch)
        );

        const results = await lastValueFrom(
            forkJoin(requests).pipe(
                map(responses => responses.flat())
            )
        );

        return results;
    }



    async updatePrices(priceChanges: PriceChange): Promise<any> {
        let url = this.BASE_URL + this.updatePricesUrl;
        let data$ = this.http.post(url, priceChanges, { responseType: 'text' })
        const value = await lastValueFrom(data$);
        return value;
    }

    async getProductHistory(storeID: number, prodNum: number): Promise<any> {
        let url = this.BASE_URL + this.productHistoryUrl;
        let data$ = this.http.get(url, {
            params: {
                storeID: storeID,
                prodNum: prodNum,
            }
        })
        const value = await lastValueFrom(data$);
        return value;
    }


    async priceMatch(storeFrom, storeTo, selectedCategories): Promise<any> {
        let url = this.BASE_URL + this.priceMatchUrl;
        const body = {
            storeIDFrom: storeFrom,
            storeIDTo: storeTo,
            selectedCategories: selectedCategories
        };

        let data$ = this.http.post(url, body);

        const value = await lastValueFrom(data$);
        return value;
    }

    async getPriceChangeLabels(conceptID: number): Promise<any> {
        let url = this.BASE_URL + this.priceChangeLabelsUrl;
        let priceChangeLabelsUrlValid = url.replace('{conceptID}', conceptID.toString());
        let data$ = this.http.get(priceChangeLabelsUrlValid);
        const value = await lastValueFrom(data$);
        return value;
    }

    async getPriceChangeLabelsByDates(conceptID: number, startDate: string, endDate: string): Promise<any> {
        let url = this.BASE_URL + this.priceChangeLabelsByDatesUrl;
        let priceChangeLabelsByDatesUrlValid = url.replace('{conceptID}', conceptID.toString());
        let data$ = this.http.get(priceChangeLabelsByDatesUrlValid,
            {
                params: {
                    startDate: startDate,
                    endDate: endDate
                }
            },
        );
        const value = await lastValueFrom(data$);
        return value;
    }

    async addPriceChangeLabel(priceChange: PriceChangeLabel): Promise<any> {
        let url = this.BASE_URL + this.addPriceChangeLabelUrl;
        let data$ = this.http.post(url, priceChange)
        const value = await lastValueFrom(data$);
        return value;
    }

    async getStorePriceChangeLabel(conceptID: number, label: string): Promise<any> {
        let url = this.BASE_URL + this.getStoreRecordsByLabelsUrl;

        let data$ = this.http.get(url, {
            params: {
                conceptID: conceptID,
                label: label,
            }
        })
        const value = await lastValueFrom(data$);
        return value;
    }

    async cancelPriceChange(storeID: number, posToDoID: number, label: string): Promise<any> {
        let url = this.BASE_URL + this.cancelPriceChangeByStore;
        const body = {
            storeID: storeID,
            posToDoID: posToDoID,
            label: label
        };
        let data$ = this.http.post(url, body, { responseType: 'text' });
        const value = await lastValueFrom(data$);
        return value;
    }


    async reschedulePriceChange(storeID: number, posToDoID: number, when2Execute: string): Promise<any> {
        let url = this.BASE_URL + this.reschedulePriceChangeByStore;
        const body = {
            storeID: storeID,
            posToDoID: posToDoID,
            when2Execute: when2Execute
        };
        let data$ = this.http.post(url, body, { responseType: 'text' });
        const value = await lastValueFrom(data$);
        return value;
    }

    async reschedulePriceChangeWithNewLabel(when2Execute: string, label: any, conceptID: number, storeRecords: { StoreID: number, PosToDoID: number }[]): Promise<any> {
        const url = this.BASE_URL + this.reschedulePriceChangeNewLabel;
        const body = {
            when2Execute,
            label,
            conceptID,
            storeRecords
        };
        let data$ = this.http.post(url, body, { responseType: 'text' });
        const value = await lastValueFrom(data$);
        return value;
    }


}